import "./journal-section-second-filter.scss";
import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import {
  FiltersMenuDate,
  FiltersMenuRow,
  FiltersMenuTags,
  JournalActiveFilterChips,
  JournalFiltersMenu,
  formatJournalFilterDate,
  type JournalActiveFilterChip,
} from "@/features/journal-filters-menu";
import { FiltersButton, InputSearchWithInfo } from "@shared/ui";

const STATUS_OPTIONS = [
  { id: "NEW", label: "Новый" },
  { id: "AWAITING_SIGNATURE", label: "На подписании" },
  { id: "SIGNED", label: "Подписан" },
  { id: "SENT_TO_GSN", label: "Отправлено" },
  { id: "REJECTED", label: "Отклонено" },
];

type Signer = {
  representativeId?: string;
  user?: {
    userId?: string;
    surname?: string;
    firstName?: string;
    middleName?: string;
  };
};

type Draft = {
  statuses: string[];
  signers: string[];
  specialJournalDate: string | null;
  dateHandover: string | null;
};

const emptyDraft = (): Draft => ({
  statuses: [],
  signers: [],
  specialJournalDate: null,
  dateHandover: null,
});

const formatSignerLabel = (signer: Signer) => {
  const surname = signer.user?.surname || "";
  const firstInitial = signer.user?.firstName ? `${signer.user.firstName[0]}.` : "";
  const middleInitial = signer.user?.middleName ? `${signer.user.middleName[0]}.` : "";
  return `${surname} ${firstInitial}${middleInitial}`.trim();
};

const getSignerId = (signer: Signer) => signer.user?.userId ?? signer.representativeId ?? "";

export const JournalSectionSecondFilter = ({
  onSearchChange,
  selectedStatuses,
  setSelectedStatuses,
  signerIds,
  setSignerIds,
  specialJournalDate,
  setSpecialJournalDate,
  dateHandover,
  setDateHandover,
  signersList,
  onFiltersApply,
  bulkSendMode = false,
}: {
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  specialJournalDate: string;
  setSpecialJournalDate: Dispatch<SetStateAction<string>>;
  dateHandover: string;
  setDateHandover: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFiltersApply?: () => void;
  bulkSendMode?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const hasActiveFilters =
    (!bulkSendMode && selectedStatuses.length > 0) ||
    signerIds.length > 0 ||
    Boolean(specialJournalDate || dateHandover);

  const openModal = () => {
    setDraft({
      statuses: selectedStatuses,
      signers: signerIds,
      specialJournalDate: specialJournalDate || null,
      dateHandover: dateHandover || null,
    });
    setOpen(true);
  };

  const applyFilters = () => {
    setSelectedStatuses(draft.statuses);
    setSignerIds(draft.signers);
    setSpecialJournalDate(draft.specialJournalDate || "");
    setDateHandover(draft.dateHandover || "");
    setOpen(false);
    onFiltersApply?.();
  };

  const resetFilters = () => {
    const cleared = emptyDraft();
    setDraft(cleared);
    setSelectedStatuses([]);
    setSignerIds([]);
    setSpecialJournalDate("");
    setDateHandover("");
    setOpen(false);
    onFiltersApply?.();
  };

  return (
    <div className="journal-section-second-filter">
      <InputSearchWithInfo
        className="journal-section-second-filter__search"
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ fontSize: "12px" }}
        tooltipTitle="Поиск по наименованию журнала"
      />
      <FiltersButton active={hasActiveFilters} onClick={openModal} />

      <JournalFiltersMenu
        open={open}
        onClose={() => setOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        {!bulkSendMode ? (
          <FiltersMenuRow
            stacked
            label="Статус записи"
            onClear={
              draft.statuses.length
                ? () => setDraft((prev) => ({ ...prev, statuses: [] }))
                : undefined
            }
          >
            <FiltersMenuTags
              options={STATUS_OPTIONS}
              value={draft.statuses}
              onChange={(statuses) => setDraft((prev) => ({ ...prev, statuses }))}
            />
          </FiltersMenuRow>
        ) : null}

        <FiltersMenuRow
          stacked
          label="Подписант"
          onClear={
            draft.signers.length
              ? () => setDraft((prev) => ({ ...prev, signers: [] }))
              : undefined
          }
        >
          <FiltersMenuTags
            options={signerOptions}
            value={draft.signers}
            onChange={(signers) => setDraft((prev) => ({ ...prev, signers }))}
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Дата создания журнала">
          <FiltersMenuDate
            value={draft.specialJournalDate}
            onChange={(specialJournalDateValue) =>
              setDraft((prev) => ({
                ...prev,
                specialJournalDate: specialJournalDateValue as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Дата передачи журнала">
          <FiltersMenuDate
            value={draft.dateHandover}
            onChange={(dateHandoverValue) =>
              setDraft((prev) => ({
                ...prev,
                dateHandover: dateHandoverValue as string | null,
              }))
            }
          />
        </FiltersMenuRow>
      </JournalFiltersMenu>
    </div>
  );
};

export const JournalSectionSecondActiveFilterChips = ({
  selectedStatuses,
  setSelectedStatuses,
  signerIds,
  setSignerIds,
  specialJournalDate,
  setSpecialJournalDate,
  dateHandover,
  setDateHandover,
  signersList,
  onFilterRemove,
  bulkSendMode = false,
}: {
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  specialJournalDate: string;
  setSpecialJournalDate: Dispatch<SetStateAction<string>>;
  dateHandover: string;
  setDateHandover: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFilterRemove?: () => void;
  bulkSendMode?: boolean;
}) => {
  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const removeFilter = (updater: () => void) => {
    updater();
    onFilterRemove?.();
  };

  const chips: JournalActiveFilterChip[] = [];

  if (!bulkSendMode) {
    selectedStatuses.forEach((statusId) => {
    const option = STATUS_OPTIONS.find((item) => item.id === statusId);
    if (!option) return;
    chips.push({
      key: `status-${statusId}`,
      filterLabel: "Статус записи",
      value: option.label,
      onRemove: () =>
        removeFilter(() =>
          setSelectedStatuses((prev) => prev.filter((item) => item !== statusId)),
        ),
    });
  });
  }

  signerIds.forEach((signerId) => {
    const option = signerOptions.find((item) => item.id === signerId);
    if (!option) return;
    chips.push({
      key: `signer-${signerId}`,
      filterLabel: "Подписант",
      value: option.label,
      onRemove: () =>
        removeFilter(() =>
          setSignerIds((prev) => prev.filter((item) => item !== signerId)),
        ),
    });
  });

  if (specialJournalDate) {
    chips.push({
      key: "special-journal-date",
      filterLabel: "Дата создания журнала",
      value: formatJournalFilterDate(specialJournalDate),
      onRemove: () => removeFilter(() => setSpecialJournalDate("")),
    });
  }

  if (dateHandover) {
    chips.push({
      key: "date-handover",
      filterLabel: "Дата передачи журнала",
      value: formatJournalFilterDate(dateHandover),
      onRemove: () => removeFilter(() => setDateHandover("")),
    });
  }

  return <JournalActiveFilterChips chips={chips} />;
};
